/*@flow*/
import * as I from 'immutable';
import keyStartsWith from 'lib/keyStartsWith';
import ProjectPopup from 'comps/ProjectPopup';
import useCommands from 'comps/Left2/useCommands';
import { useEffect, useState } from 'react';
import wellknown from 'wellknown';
import { camelizeKeys } from 'humps';
import history from 'lib/history';
import truthy from 'lib/truthy';
import falsy from 'lib/falsy';

window.falsy = falsy;

type ProjectT = {|
  id: ID,
  status: string,
  page_type: string,
  category: string,
  latlon: number[],
  radius_meters?: number,
  map_label?: string,
  the_geom: string,
  fillColor: string,
  strokeColor: string,
|};

type ProjectDeckType = {|
  category: string,
  className: string,
  color: string,
  fillColor: string,
  id: ID,
  latlon: Array<number>,
  map_label?: string,
  on: {| click: () => any |},
  page_type: string,
  popupOptions: {|
    component: any,
    enabled: boolean,
    lonlat: Array<number>,
  |},
  radius: number,
  radius_meters?: number,
  status: string,
  strokeColor: string,
  the_geom: string,
  type: string,
  weight: number,
|};

type ProjectDeckType2 = {|
  category: string,
  className: string,
  color: string,
  fillColor: string,
  fillOpacity: number,
  geojson: any,
  id: ID,
  latlon: Array<number>,
  map_label?: string,
  on: {| click: () => void |},
  page_type: string,
  popupOptions: {|
    component: any,
    enabled: boolean,
    lonlat: Array<number>,
  |},
  radius_meters?: number,
  status: string,
  strokeColor: string,
  the_geom: string,
  type: string,
  weight: number,
|};

type ProjectDeckType3 = {|
  category: string,
  className: string,
  color: string,
  fillColor: string,
  id: ID,
  latlon: Array<number>,
  map_label?: string,
  on: {| click: () => any |},
  page_type: string,
  popupOptions: {|
    component: any,
    enabled: boolean,
    lonlat: Array<number>,
  |},
  radius: number,
  radius_meters?: number,
  status: string,
  strokeColor: string,
  the_geom: string,
  type: string,
  weight: number,
|};

// mkLabelConfig builds a configuration for a label on Parchment
// map. It assumes the config will be turned into an Immutable map.
export const mkLabelConfig = (o: {|
  ...ProjectT,
  enabled: boolean,
|}): [
  string,
  {|
    content: void | string,
    enabled: boolean,
    id: ID,
    latlon: Array<number>,
    offset: Array<number>,
    type: string,
  |},
] => [
  `project-${o.id}-label`,
  {
    id: o.id,
    type: 'label',
    content: o.map_label,
    latlon: o.latlon,
    offset: [-20, 20],
    enabled: o.enabled,
  },
];

// mkProjectCircle builds a configuration for a project on Parchment
// map. It assumes the config will be turned into an Immutable map.
export const mkProjectCircle = (
  o: ProjectT,
  onClick: (any) => any
): [string, ProjectDeckType3] => {
  let config = {
    type: 'circlemarker',
    radius: 5,
    className: 'project-dot',
    fillColor: 'transparent',
    ...o,
    // HACK: This should come from config.
    weight: o.strokeColor ? 2 : 1,
    color: o.strokeColor || 'white',
    on: {
      click: () => onClick(camelizeKeys(o)),
    },
  };

  // Show popup
  config = {
    ...config,
    popupOptions: {
      enabled: true,
      component: ProjectPopup,
      lonlat: [o.latlon[1], o.latlon[0]],
    },
  };

  return [`project-${o.id}-circle`, config];
};

// mkProjectShape builds a configuration for a project on Parchment
// map. It assumes the config will be turned into an Immutable map.
export const mkProjectShape = (
  o: ProjectT,
  onClick: (any) => any
):
  | null
  | [string, ProjectDeckType2]
  | [string, ProjectDeckType]
  | [string, ProjectDeckType3] => {
  if (o.the_geom.match('POINT')) {
    const ret = (mkProjectCircle(o, onClick): any);
    return (ret: [string, ProjectDeckType3]);
  }
  const latlon = o.latlon || [];
  const [lat, lon] = latlon;
  if (!lat || !lon) return null;

  let config = {
    type: 'polygon',
    geojson: wellknown.parse(o.the_geom),
    className: 'project-shape',
    fillColor: 'transparent',
    fillOpacity: 0.5,
    ...o,
    // HACK: This should come from config.
    weight: o.strokeColor ? 2 : 1,
    color: o.strokeColor || 'white',
    on: {
      click: () => {
        onClick(o);
      },
    },
  };

  // Show popup
  config = {
    ...config,
    popupOptions: {
      enabled: true,
      component: ProjectPopup,
      lonlat: [lon, lat],
    },
  };
  const key = `project-${o.id}-shape`;
  return ([key, config]: [string, ProjectDeckType2]);
};

function moveShapeDecksToBack(newProjects) {
  // If there are any shapes...
  if (newProjects.some((_, k: string) => k.match(/-shape$/))) {
    // Re-order map ensuring the shapes come first
    const comparatorValueMapper = (_, k: string) =>
      k.match(/-shape$/) ? 0 : 1;
    newProjects = newProjects.sortBy(comparatorValueMapper);

    let iter = 0;
    newProjects = newProjects.mapKeys((k) => {
      const nk = `${k}-${iter++}`;
      return nk;
    });
  }
  return newProjects;
}

// deckConfig casts the config body into an Immutable object. This is done
// to permit cleaner snapshot tests of config objects.
const deckConfig = (arr) => [arr[0], I.fromJS(arr[1])];

type FactoryType = (
  ProjectT,
  (ProjectT) => any
) =>
  | null
  | [string, ProjectDeckType]
  | [string, ProjectDeckType2]
  | [string, ProjectDeckType3];

export function mkBuilder(
  factory: FactoryType,
  onClick: (any) => any,
  isLabelEnabled: boolean = false
): (ProjectT[]) => I.Map<string, {}> {
  return (projects: ProjectT[]) => {
    if (!factory) return I.Map();

    let labels = [];
    // Add a label if map_label is not null.
    const addLabel = (o) => {
      if (o.map_label) {
        const label = mkLabelConfig({ ...o, enabled: isLabelEnabled });
        const config = deckConfig(label);
        config && labels.push(config);
      }
    };

    let newProjects = I.Map(
      projects
        .map((o: ProjectT) => {
          addLabel(o);
          const config = factory(o, onClick);
          return config ? deckConfig(config) : null;
        })
        .filter(Boolean)
    );

    const labelDecks = (I: any).Map(labels);
    // Filter out existing projects and concatenate new project decks
    // to non-project decks.
    newProjects = newProjects.merge(labelDecks);

    newProjects = moveShapeDecksToBack(newProjects);

    return newProjects;
  };
}

export const removeProjectPrefixedDecksAndMerge = (
  d: any,
  d2: I.Map<string, {}> = I.Map()
): any => {
  return d.filterNot(keyStartsWith('project-')).merge(d2);
};

function useLoad() {
  return history.getQuery().load || {};
}

function useShowShapes() {
  const load = useLoad();
  const { showShapes } = load;
  return truthy(showShapes);
}

function useLabelEnabled() {
  const load = useLoad();
  const { showLabels } = load;
  return truthy(showLabels);
}

function useBuilder(factory, projects, show) {
  const cmd = useCommands();
  const [d, setD] = useState(I.Map());

  const isLabelEnabled = useLabelEnabled();

  useEffect(() => {
    if (show && factory) {
      const onClick = (project) => {
        cmd.showItemView(project);
      };
      if (factory !== null) {
        const builder = mkBuilder(factory, onClick, isLabelEnabled);
        setD(() => builder(projects));
      }
    } else {
      setD(I.Map());
    }
  }, [projects, show, isLabelEnabled, factory]);

  return d;
}

function useCircles(projects) {
  const showShapes = useShowShapes();
  const show = showShapes === false;
  const factory = mkProjectCircle;
  return useBuilder(factory, projects, show);
}

function useShapes(projects) {
  const show = useShowShapes();
  const factory = mkProjectShape;
  return useBuilder(factory, projects, show);
}

function useProjectLayers(projects: ProjectT[] = []) {
  const cmd = useCommands();
  const circleDecks = useCircles(projects);
  const shapeDecks = useShapes(projects);

  useEffect(() => {
    cmd.set('decks', (d) => {
      if (!d) return d;
      const d2 = circleDecks.merge(shapeDecks);
      return removeProjectPrefixedDecksAndMerge(d, d2);
    });
  }, [circleDecks, shapeDecks]);
}

export default useProjectLayers;
